'use client';

import * as React from 'react';
import { format, getDaysInMonth, setMonth, setYear, setDate as setDay, isValid } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import { WheelPicker, WheelPickerWrapper } from '@/components/wheel-picker';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { useFieldVariant } from '@/components/ui/field-variant';
import { FIELD_CHROME, type FieldVariant } from '@/lib/field-chrome';

interface WheelDatePickerProps {
  value?: Date | null;
  onChange: (date: Date) => void;
  minYear?: number;
  maxYear?: number;
  className?: string;
}

const MONTHS = [
  { label: 'January', value: '0' },
  { label: 'February', value: '1' },
  { label: 'March', value: '2' },
  { label: 'April', value: '3' },
  { label: 'May', value: '4' },
  { label: 'June', value: '5' },
  { label: 'July', value: '6' },
  { label: 'August', value: '7' },
  { label: 'September', value: '8' },
  { label: 'October', value: '9' },
  { label: 'November', value: '10' },
  { label: 'December', value: '11' },
];

export function WheelDatePicker({
  value,
  onChange,
  minYear = 1900,
  maxYear = new Date().getFullYear(),
  className,
}: WheelDatePickerProps) {
  const date = value && isValid(value) ? value : new Date();

  const [selectedMonth, setSelectedMonth] = React.useState(
    isNaN(date.getMonth()) ? '0' : date.getMonth().toString()
  );
  const [selectedDay, setSelectedDay] = React.useState(
    isNaN(date.getDate()) ? '1' : date.getDate().toString()
  );
  const [selectedYear, setSelectedYear] = React.useState(
    isNaN(date.getFullYear()) ? new Date().getFullYear().toString() : date.getFullYear().toString()
  );

  // Update internal state when value prop changes
  React.useEffect(() => {
    if (value && isValid(value)) {
      setSelectedMonth(value.getMonth().toString());
      setSelectedDay(value.getDate().toString());
      setSelectedYear(value.getFullYear().toString());
    }
  }, [value]);

  const years = React.useMemo(() => {
    const yearsArr = [];
    for (let i = minYear; i <= maxYear; i++) {
      yearsArr.push({ label: i.toString(), value: i.toString() });
    }
    return yearsArr;
  }, [minYear, maxYear]);

  const days = React.useMemo(() => {
    const daysInMonth = getDaysInMonth(new Date(parseInt(selectedYear), parseInt(selectedMonth)));
    const daysArr = [];
    for (let i = 1; i <= daysInMonth; i++) {
      daysArr.push({ label: i.toString().padStart(2, '0'), value: i.toString() });
    }
    return daysArr;
  }, [selectedMonth, selectedYear]);

  const handleMonthChange = (newMonth: string) => {
    setSelectedMonth(newMonth);
    const newDate = setMonth(date, parseInt(newMonth));

    // Ensure day is still valid for the new month
    const daysInNewMonth = getDaysInMonth(newDate);
    let finalDate = newDate;
    if (parseInt(selectedDay) > daysInNewMonth) {
      setSelectedDay(daysInNewMonth.toString());
      finalDate = setDay(newDate, daysInNewMonth);
    }

    onChange(finalDate);
  };

  const handleDayChange = (newDay: string) => {
    setSelectedDay(newDay);
    onChange(setDay(date, parseInt(newDay)));
  };

  const handleYearChange = (newYear: string) => {
    setSelectedYear(newYear);
    const newDate = setYear(date, parseInt(newYear));

    // Ensure day is still valid (handle Feb 29 on non-leap years)
    const daysInNewMonth = getDaysInMonth(newDate);
    let finalDate = newDate;
    if (parseInt(selectedDay) > daysInNewMonth) {
      setSelectedDay(daysInNewMonth.toString());
      finalDate = setDay(newDate, daysInNewMonth);
    }

    onChange(finalDate);
  };

  return (
    <div className={cn('flex flex-col items-center mx-auto', className)}>
      <WheelPickerWrapper className="flex flex-row w-[300px] h-64 bg-background border rounded-xl overflow-hidden shadow-sm">
        <div className="flex-1 relative border-r border-border/50">
          <WheelPicker
            options={days}
            value={selectedDay}
            onValueChange={handleDayChange}
            className="w-full"
          />
        </div>
        <div className="flex-[2] relative">
          <WheelPicker
            options={MONTHS}
            value={selectedMonth}
            onValueChange={handleMonthChange}
            className="w-full"
          />
        </div>
        <div className="flex-[1.5] relative border-l border-border/50">
          <WheelPicker
            options={years}
            value={selectedYear}
            onValueChange={handleYearChange}
            className="w-full"
          />
        </div>
      </WheelPickerWrapper>
    </div>
  );
}

interface WheelDatePickerFieldProps extends WheelDatePickerProps {
  placeholder?: string;
  variant?: FieldVariant;
  onCommit?: () => void;
}

const DATE_PICKER_VARIANT = {
  solid: `${FIELD_CHROME.solid.base} ${FIELD_CHROME.solid.focusVisible}`,
  glass: `${FIELD_CHROME.glass.base} ${FIELD_CHROME.glass.focusVisible}`,
} as const;

export function WheelDatePickerField({
  value,
  onChange,
  placeholder = 'Select date',
  variant,
  className,
  onCommit,
  ...props
}: WheelDatePickerFieldProps) {
  const [open, setOpen] = React.useState(false);
  const resolvedVariant = useFieldVariant(variant);
  const pendingDateRef = React.useRef<Date | null>(null);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && pendingDateRef.current) {
      onChange(pendingDateRef.current);
      onCommit?.();
      pendingDateRef.current = null;
    }
    setOpen(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant={'outline'}
          className={cn(
            'h-11 w-full justify-start rounded-xl text-left font-normal',
            DATE_PICKER_VARIANT[resolvedVariant],
            !value && 'text-muted-foreground dark:text-neutral-400',
            value && isValid(value) && 'text-foreground dark:text-neutral-100',
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {value && isValid(value) ? format(value, 'PPP') : <span>{placeholder}</span>}
        </Button>
      </DialogTrigger>
      <DialogContent
        syncBrowserHistory={false}
        className="max-w-[min(100vw-2rem,400px)] w-full gap-4"
      >
        <DialogHeader className="text-center sm:text-center">
          <DialogTitle>{placeholder}</DialogTitle>
        </DialogHeader>
        <div className="space-y-6">
          <WheelDatePicker
            value={value}
            onChange={(date) => {
              pendingDateRef.current = date;
              onChange(date);
            }}
            {...props}
          />
          <Button
            type="button"
            className="w-full h-12 rounded-xl text-lg font-semibold shadow-lg shadow-primary/20"
            onClick={() => handleOpenChange(false)}
          >
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
